import React, { useState } from 'react';
import {
    Button,
    Typography,
    Stack,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Divider,
    Chip,
    Alert,
    IconButton,
} from '@mui/material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { ArrowBack, UploadFile, CheckCircle } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../../config/app.config';
import { useWarehouses } from '../../../hooks/useWarehouseOperations';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import { container } from '@infrastructure/container';
import { FilePreviewData } from '../../../services/types';

interface FileSummaryFormProps {
    onClose: () => void;
    onBack: () => void;
    fileData?: {
        fileType: 'csv' | 'excel' | null;
        file: File;
        previewData: FilePreviewData;
    } | null;
}

const FileSummaryForm: React.FC<FileSummaryFormProps> = ({
    onClose,
    onBack,
    fileData,
}) => {
    const [form, setForm] = useState({
        name: '',
        location: '',
    });

    const { createWarehouse, isCreating } = useWarehouses();
    const [isUploading, setIsUploading] = useState(false);

    const handleSubmit = async () => {
        if (!fileData) return;

        try {
            if (!previewData.columnMapping?.ean || !previewData.columnMapping?.quantity || previewData.headerRowIndex === undefined || !fileType) {
                throw new Error('Column mapping is not valid');
            }

            const warehouseObject = {
                name: form.name,
                location: form.location,
                warehouseConnections: [
                    {
                        mutationType: MutationType.SYNC,
                        sourceType: WarehouseConnectionType.FILE,
                        rules: [
                            {
                                field: RuleField.FILE_TYPE,
                                operator: RuleOperator.EQUALS,
                                value: fileType,
                            },
                            {
                                field: RuleField.HEADER_ROW_INDEX,
                                operator: RuleOperator.EQUALS,
                                value: previewData.headerRowIndex,
                            },
                            {
                                field: RuleField.EAN_COLUMN_NAME,
                                operator: RuleOperator.EQUALS,
                                value: previewData.columnMapping.ean,
                            },
                            {
                                field: RuleField.QUANTITY_COLUMN_NAME,
                                operator: RuleOperator.EQUALS,
                                value: previewData.columnMapping.quantity,
                            },
                            ...(previewData.delimiter ? [{
                                field: RuleField.DELIMITER,
                                operator: RuleOperator.EQUALS,
                                value: previewData.delimiter,
                            }] : []),
                            ...(previewData.selectedSheet ? [{
                                field: RuleField.SHEET_NAME,
                                operator: RuleOperator.EQUALS,
                                value: previewData.selectedSheet,
                            }] : [])
                        ]
                    }

                ]
            };


            const warehouse = await createWarehouse(warehouseObject)

            setIsUploading(true);

            const warehouseConnection = warehouse.warehouseConnections?.find(connection => connection.sourceType === WarehouseConnectionType.FILE);


            if (warehouseConnection) {
                await container.inventoryRepository.submitFileMutation(file, warehouseConnection.id);
            }

            onClose();
        } catch (error) {
            console.error('Failed to create warehouse:', error);
        } finally {
            setIsUploading(false);
        }
    };

    if (!fileData) {
        return (
            <Alert severity="error">
                No file data available. Please go back and upload a file.
            </Alert>
        );
    }

    const { file, previewData, fileType } = fileData;

    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center">
                        <IconButton onClick={onBack} color="inherit">
                            <ArrowBack />
                        </IconButton>

                        <Typography variant="h6" component="div">
                            Create File Warehouse
                        </Typography>
                    </Stack>
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
                <Stack gap={3} sx={{ mt: 2 }}>

                    {/* Warehouse Details Form */}
                    <Box>
                        <Typography variant="subtitle1" color="text.primary" sx={{ mb: 2 }}>
                            Warehouse Details
                        </Typography>
                        <Stack gap={2}>
                            <TextField
                                label="Warehouse name"
                                value={form.name}
                                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                size="small"
                                placeholder="e.g., Main Warehouse"
                            />
                            <TextField
                                label="Location"
                                value={form.location}
                                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                                fullWidth
                                size="small"
                                placeholder="e.g., Amsterdam, Netherlands"
                            />
                        </Stack>
                    </Box>


                    <Divider />

                    {/* File Summary Card */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint,
                        border: '1px solid',
                        borderColor: APP_CONFIG.ui.theme.colors.border,
                        borderRadius: 1,
                    }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                            <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: 1,
                                backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid',
                                borderColor: APP_CONFIG.ui.theme.colors.border,
                            }}>
                                <UploadFile sx={{
                                    color: APP_CONFIG.ui.theme.colors.primary,
                                    fontSize: 28
                                }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 500 }}>
                                    {file.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {file.name.toLowerCase().endsWith('.csv') ? 'CSV File' : 'Excel File'}
                                </Typography>
                            </Box>
                        </Stack>



                        <Typography variant="subtitle1" color="text.primary" sx={{ mb: 2 }}>
                            Column Mapping
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Chip
                                icon={<CheckCircle />}
                                label={`EAN: ${previewData.columnMapping?.ean || 'Not mapped'}`}
                                color={previewData.columnMapping?.ean ? 'success' : 'default'}
                                variant={previewData.columnMapping?.ean ? 'filled' : 'outlined'}
                            />
                            <Chip
                                icon={<CheckCircle />}
                                label={`Quantity: ${previewData.columnMapping?.quantity || 'Not mapped'}`}
                                color={previewData.columnMapping?.quantity ? 'success' : 'default'}
                                variant={previewData.columnMapping?.quantity ? 'filled' : 'outlined'}
                            />
                        </Stack>
                    </Box>



                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={
                        !form.name ||
                        isCreating ||
                        isUploading
                    }
                >
                    {isCreating ? 'Creating...' : isUploading ? 'Uploading...' : 'Create Warehouse'}
                </Button>
            </DialogActions>
        </>
    );
};

export default FileSummaryForm; 